import React, { useRef, useState } from "react";
import { Upload, FolderOpen, File, Folder, Folders } from 'lucide-react';
import {
	FileUploadContainer,
	FormField,
	DragDropText,
	UploadFileBtn,
	FilePreviewContainer,
	ImagePreview,
	PreviewContainer,
	PreviewList,
	FileMetaData,
	RemoveFileIcon,
	InputLabel,
	ButtonWrapper
} from "./FileUpload.styles";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';

const KILO_BYTES_PER_BYTE = 1000;
const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 5000000;
const ariaLabel = { 'aria-label': 'description' };

const convertNestedObjectToArray = (nestedObj) =>
	Object.keys(nestedObj).map((key) => nestedObj[key]);

const convertBytesToKB = (bytes) => Math.round(bytes / KILO_BYTES_PER_BYTE);

const FileUpload = ({
	trackRadio,
	label,
	updateFilesCb,
	maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
	...otherProps
}) => {
	const fileInputField = useRef(null);
	const [files, setFiles] = useState({});
	const [isDragging, setIsDragging] = useState(false);

	const handleUploadBtnClick = () => {
		fileInputField.current.click();
	};

	const addNewFiles = (newFiles) => {
		for (let file of newFiles) {
			if (file.size < maxFileSizeInBytes) {
				if (!otherProps.multiple) {
					return { file };
				}
				files[file.name] = file;
			}
		}
		return { ...files };
	};

	const callUpdateFilesCb = (files) => {
		const filesAsArray = convertNestedObjectToArray(files);
		updateFilesCb(filesAsArray);
	};

	const handleNewFileUpload = (e) => {
		const { files: newFiles } = e.target;
		if (newFiles.length) {
			let updatedFiles = addNewFiles(newFiles);
			setFiles(updatedFiles);
			callUpdateFilesCb(updatedFiles);
		}
	};

	const removeFile = (fileName) => {
		delete files[fileName];
		setFiles({ ...files });
		callUpdateFilesCb({ ...files });
	};

	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isDragging) {
			setIsDragging(true);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const { files: droppedFiles } = e.dataTransfer;
		if (droppedFiles.length) {
			let updatedFiles = addNewFiles(droppedFiles);
			setFiles(updatedFiles);
			callUpdateFilesCb(updatedFiles);
		}
	};

	return (
		<>
			<FileUploadContainer
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				style={{ 
					borderColor: isDragging ? '#3498db' : 'lightgray',
					backgroundColor: isDragging ? 'rgba(52, 152, 219, 0.1)' : '#27365d1f'
				}}
			>
				<InputLabel>{label}</InputLabel>
				<DragDropText>Drag and drop your files or</DragDropText>
				<ButtonWrapper onClick={handleUploadBtnClick}>
					<UploadFileBtn className="positive" type="button">
						<FolderOpen size={44} />
						<span>Browse Files</span>
					</UploadFileBtn>
					<FormField
						ref={fileInputField}
						type="file"
						id="file"
						name="file"
						onChange={handleNewFileUpload}
						accept=".csv,.xlsx,.xls"
						disabled={otherProps.isLoading}
					/>
				</ButtonWrapper>
				<FormControl>
					<RadioGroup
						row
						aria-labelledby="demo-row-radio-buttons-group-label"
						name="row-radio-buttons-group"
						onChange={trackRadio}
						defaultValue="reprice"
					>
						<FormControlLabel value="reprice" control={<Radio />} label="Reprice" />
						<FormControlLabel value="filter" control={<Radio />} label="Filter" />
						<FormControlLabel value="report" control={<Radio />} label="Report" />
						<FormControlLabel value="scandx" control={<Radio />} label="ScanDX" />
					</RadioGroup>
				</FormControl>
			</FileUploadContainer>

			<FilePreviewContainer>
				<span>To Upload</span>
				<PreviewList>
					{Object.keys(files).map((fileName, index) => {
						let file = files[fileName];
						let isImageFile = file.type.split("/")[0] === "image";
						return (
							<PreviewContainer key={fileName}>
								<div>
									{isImageFile && (
										<ImagePreview
											src={URL.createObjectURL(file)}
											alt={`file preview ${index}`}
										/>
									)}
									<FileMetaData isImageFile={isImageFile}>
										<span>{file.name}</span>
										<aside>
											<span>{convertBytesToKB(file.size)} kb</span>
											<RemoveFileIcon
												className="fas fa-trash-alt"
												onClick={() => removeFile(fileName)}
											/>
										</aside>
									</FileMetaData>
								</div>
							</PreviewContainer>
						);
					})}
				</PreviewList>
			</FilePreviewContainer>
		</>
	);
};

export default FileUpload;